'use client';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { Link } from '@/i18n/routing';

interface ProductItem {
  slug: string;
  href: string;
  image: string;
  titleKey: string;
}

const PRODUCT_ITEMS: ProductItem[] = [
  {
    slug: 'ai-text-to-video',
    href: '/ai-text-to-video',
    image: 'https://image01.cf.vidu.studio/vidu/media-asset/banner1-c213f062.webp',
    titleKey: 'ai-text-to-video.title',
  },
  {
    slug: 'ai-image-to-video',
    href: '/ai-image-to-video',
    image: 'https://image01.cf.vidu.studio/vidu/media-asset/banner-486db1f7.webp',
    titleKey: 'ai-image-to-video.title',
  },
  {
    slug: 'ai-reference-to-video',
    href: '/ai-reference-to-video',
    image: 'https://image01.cf.vidu.studio/vidu/media-asset/banner-ac011111.webp',
    titleKey: 'ai-reference-to-video.title',
  },
  {
    slug: 'ai-image-generator',
    href: '/ai-image-generator',
    image: 'https://image01.cf.vidu.studio/vidu/media-asset/ai-image-generate-90d267f5.webp',
    titleKey: 'ai-image-generator.title',
  },
  {
    slug: 'ai-sound-effect-generator',
    href: '/ai-sound-effect-generator',
    image: 'https://image01.cf.vidu.studio/vidu/media-asset/hero-b72c3f46-026140cd.webp',
    titleKey: 'ai-sound-effect-generator.title',
  },
  {
    slug: 'ai-templates',
    href: '/ai-templates',
    image: 'https://image01.cf.vidu.studio/vidu/media-asset/ai-template-a51088fa.webp',
    titleKey: 'ai-templates.title',
  },
  {
    slug: 'vidu-q3',
    href: '/vidu-q3',
    image: 'https://image01.cf.vidu.studio/vidu/media-asset/vidu-q3-511e518a.webp',
    titleKey: 'vidu-q3.title',
  },
  {
    slug: 'vidu-claw',
    href: '/vidu-claw',
    image: 'https://image01.vidu.zone/vidu/media-asset/claw-product-header-img-206e7e18-aaaf5e43.webp',
    titleKey: 'vidu-claw.title',
  },
];

const PAGE_SIZE = 4;

export function RelatedProductsSection({ currentSlug }: { currentSlug: string }) {
  const t = useTranslations('meta');
  const tSection = useTranslations('relatedProducts');
  const items = PRODUCT_ITEMS.filter((item) => item.slug !== currentSlug);
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(items.length / PAGE_SIZE);
  const visible = items.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <section className="w-full bg-black pt-16">
      <div className="mx-auto max-w-[1440px] px-8 py-12 text-center md:px-4 md:py-8">
        <h2 className="mb-10 w-full font-bold text-[40px] leading-[1.4] md:mb-8 md:font-semibold md:text-[32px]">
          {tSection('title')}
        </h2>
        <div className="relative flex items-center gap-4">
          {totalPages > 1 && (
            <button
              type="button"
              aria-label={tSection('prev')}
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/20 text-white text-xl transition-colors hover:bg-white/10 disabled:opacity-30"
            >
              <BaseLineLeft />
            </button>
          )}
          <div className="grid flex-1 grid-cols-4 gap-8 sm:grid-cols-2 sm:gap-4">
            {Array.from({ length: PAGE_SIZE }, (_, i) => visible[i] ?? null).map((item, i) => {
              if (!item) return <div key={`placeholder-p${page}-s${PAGE_SIZE - i}`} className="aspect-square" />;
              return (
                <Link
                  key={item.slug}
                  href={item.href}
                  className="group relative aspect-square overflow-hidden rounded-2xl border border-white/10"
                >
                  <Image
                    alt={t(item.titleKey)}
                    className="h-full w-full object-cover"
                    src={item.image}
                    width={320}
                    height={320}
                    loading="lazy"
                  />
                  <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-[#484848] p-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <p className="min-w-0 flex-1 font-medium text-[15px] text-white leading-[1.2]">
                      {t(item.titleKey)}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
          {totalPages > 1 && (
            <button
              type="button"
              aria-label={tSection('next')}
              disabled={page === totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/20 text-white text-xl transition-colors hover:bg-white/10 disabled:opacity-30"
            >
              <BaseLineRight />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
